import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { Copy } from 'lucide-react';
import { vdHoursApi } from '../api';

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const DAY_LABELS = { monday: 'Mon', tuesday: 'Tue', wednesday: 'Wed', thursday: 'Thu', friday: 'Fri', saturday: 'Sat', sunday: 'Sun' };

export default function VDBusinessHours() {
  const [hours, setHours] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => { vdHoursApi.get().then(d => { setHours(d); setLoading(false); }); }, []);

  const updateDay = (day, field, value) => {
    setHours(prev => ({
      ...prev,
      schedule: { ...prev.schedule, [day]: { ...prev.schedule[day], [field]: value } },
    }));
  };

  const copyToAll = () => {
    const mon = hours.schedule.monday;
    const updated = {};
    DAYS.forEach(d => { updated[d] = { ...mon }; });
    setHours(prev => ({ ...prev, schedule: updated }));
    toast.success('Monday hours copied to all days');
  };

  const onSave = async () => {
    setSaving(true);
    await vdHoursApi.update(hours);
    toast.success('Business hours saved');
    setSaving(false);
  };

  if (loading || !hours) return <div className="animate-pulse h-96 bg-card/5 rounded-xl" />;

  return (
    <div className="max-w-2xl space-y-6" data-testid="vd-business-hours">
      <div>
        <h1 className="text-2xl font-bold">Business Hours</h1>
        <p className="text-muted-foreground text-sm mt-1">Set when your phones are active</p>
      </div>

      <Card className="bg-card/5 border-white/10">
        <CardHeader className="flex flex-row items-center justify-between pb-3">
          <CardTitle className="text-base">Weekly Schedule</CardTitle>
          <Button variant="outline" size="sm" onClick={copyToAll} className="border-white/10 text-xs">
            <Copy className="w-3 h-3 mr-1" />Copy Mon to All
          </Button>
        </CardHeader>
        <CardContent className="space-y-3">
          {DAYS.map(day => (
            <div key={day} className="flex items-center gap-3">
              <div className="w-10 text-sm font-medium text-muted-foreground">{DAY_LABELS[day]}</div>
              <Switch
                checked={hours.schedule[day]?.enabled}
                onCheckedChange={v => updateDay(day, 'enabled', v)}
              />
              {hours.schedule[day]?.enabled ? (
                <>
                  <Input
                    type="time"
                    value={hours.schedule[day]?.open || '09:00'}
                    onChange={e => updateDay(day, 'open', e.target.value)}
                    className="w-32 bg-card/5 border-white/10 text-sm"
                  />
                  <span className="text-muted-foreground text-xs">to</span>
                  <Input
                    type="time"
                    value={hours.schedule[day]?.close || '17:00'}
                    onChange={e => updateDay(day, 'close', e.target.value)}
                    className="w-32 bg-card/5 border-white/10 text-sm"
                  />
                </>
              ) : (
                <span className="text-xs text-muted-foreground">Closed</span>
              )}
            </div>
          ))}
        </CardContent>
      </Card>

      <Card className="bg-card/5 border-white/10">
        <CardHeader><CardTitle className="text-base">After Hours</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div><Label>Forward To</Label><Input value={hours.afterHoursRoute || ''} onChange={e => setHours(p => ({ ...p, afterHoursRoute: e.target.value }))} className="bg-card/5 border-white/10 mt-1" placeholder="Voicemail or phone number" /></div>
          <div><Label>After-Hours Message</Label><Textarea value={hours.afterHoursMessage || ''} onChange={e => setHours(p => ({ ...p, afterHoursMessage: e.target.value }))} className="bg-card/5 border-white/10 mt-1" rows={2} /></div>
        </CardContent>
      </Card>

      <Button onClick={onSave} disabled={saving} className="bg-emerald-500 hover:bg-emerald-600 text-black font-medium">
        {saving ? 'Saving...' : 'Save Hours'}
      </Button>
    </div>
  );
}
